/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import {
	Suspense,
	useEffect,
	useCallback,
	MouseEvent,
	useState,
	type ReactNode,
} from 'react'
import { useSearchParams } from 'next/navigation'
import classNames from 'classnames'
import { vercelSplit } from '@web/cms'
import { Tabs } from '@hashicorp/mds-react/components'
import { toKebabCase } from 'lib/to-kebab-case'
import { getAlignment } from 'components/materia/sub-blocks/heading-text/utils'
import s from './style.module.css'

export const ACTIVE_TAB_SEARCH_KEY = 'tab'

interface TabsHandlerProps {
	updateUrlOnChange?: boolean
	tabIndicatorTheme?: string
	tabIndicatorAlignment?: string
	panels: { name: string; labelIcon?: { name: string } }[]
	children: ReactNode
}

export const TabsHandler = ({
	updateUrlOnChange,
	tabIndicatorTheme,
	tabIndicatorAlignment = 'left',
	panels,
	children,
}: TabsHandlerProps) => {
	const alignment = getAlignment(vercelSplit(tabIndicatorAlignment))
	const shouldShowTabList = panels.length > 1
	const searchParams = useSearchParams()
	const [initialActiveTabIndex, setInitialActiveTabIndex] = useState<number>(0)
	const [tabsInitialized, setTabsInitialized] = useState(false)

	useEffect(() => {
		if (!updateUrlOnChange) return

		const activeTab = searchParams.get(ACTIVE_TAB_SEARCH_KEY)
		const initialActiveTabIndex = panels.findIndex(
			({ name }) => toKebabCase(name) === activeTab,
		)
		if (!tabsInitialized && initialActiveTabIndex >= 0) {
			setInitialActiveTabIndex(initialActiveTabIndex)
			setTabsInitialized(true)
		}
	}, [searchParams, updateUrlOnChange])

	// This useEffect scrolls to the active tab's anchor hash (e.g., tab=vault#vault-radar-pricing)
	// The 0.1s delay lets tabs load before scrollToHash runs.
	// Without the delay, scrolling may happen too early, and the target element won't be in the DOM.

	useEffect(() => {
		if (!tabsInitialized) return

		const hash = window.location.hash
		if (!hash) return

		const scrollToHash = () => {
			const el = document.querySelector(hash)
			if (el) {
				el.scrollIntoView({ behavior: 'smooth' })
			}
		}

		const timeoutId = setTimeout(scrollToHash, 100)
		return () => clearTimeout(timeoutId)
	}, [tabsInitialized])

	const handleClickTab = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			if (!updateUrlOnChange) return

			setTabsInitialized(true)
			const params = new URLSearchParams(searchParams?.toString())
			params.set(
				ACTIVE_TAB_SEARCH_KEY,
				toKebabCase(e.currentTarget.textContent),
			)
			window.history.replaceState(null, '', `?${params.toString()}`)
		},
		[searchParams, updateUrlOnChange],
	)

	return (
		<Suspense fallback={<div className={s.skeleton} />}>
			<Tabs.Provider
				size="large"
				className={s.tabsContainer}
				initialSelectedTabIndex={initialActiveTabIndex}
				onClickTab={handleClickTab}
			>
				{shouldShowTabList && (
					<Tabs.TabList className={classNames(s.tabsList, s[alignment])}>
						{panels.map((panel, id) => {
							if ('name' in panel) {
								return (
									<Tabs.Tab
										key={id}
										trackingKey={toKebabCase(panel.name)}
										tabIndicatorTheme={
											tabIndicatorTheme as 'primary' | 'secondary'
										}
										icon={
											'labelIcon' in panel &&
											panel.labelIcon?.name &&
											vercelSplit(panel.labelIcon.name)
										}
									>
										{panel.name}
									</Tabs.Tab>
								)
							}
						})}
					</Tabs.TabList>
				)}
				{children}
			</Tabs.Provider>
		</Suspense>
	)
}
